import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { db } from '../config/firebase';
import { ArchiveLot, ArchiveSale, toDate, sellThrough, formatRealized, mapLink } from '../types/archive.types';

/**
 * PAST SALE RESULTS — /past/:saleId. Public, no sign-in.
 * Reads ONLY archiveSales/{saleId} and its lots subcollection (no customer data — see archive.types.ts).
 * Lot number, title, photo, and Sold $X / Unsold. Nothing about who bought it.
 */
type Show = 'all' | 'sold' | 'unsold';

const PastSaleResults: React.FC = () => {
  const { saleId } = useParams<{ saleId: string }>();
  const [sale, setSale] = useState<ArchiveSale | null | undefined>(undefined);
  const [lots, setLots] = useState<ArchiveLot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<Show>('all');

  useEffect(() => {
    if (!saleId) return;
    (async () => {
      try {
        const s = await getDoc(doc(db, 'archiveSales', saleId));
        if (!s.exists()) { setSale(null); return; }
        setSale({ ...(s.data() as Omit<ArchiveSale, 'id'>), id: s.id });
        const l = await getDocs(collection(db, 'archiveSales', saleId, 'lots'));
        setLots(l.docs.map((d) => ({ ...(d.data() as Omit<ArchiveLot, 'id'>), id: d.id }))
          .sort((a, b) => a.sortKey - b.sortKey || a.lotNumber.localeCompare(b.lotNumber)));
      } catch (err: any) { console.error('Past sale load error:', err); setError(err.message); }
    })();
  }, [saleId]);

  const shown = useMemo(() => (lots || []).filter((l) => show === 'all' || l.result === show), [lots, show]);

  if (error) return <div className="max-w-5xl mx-auto rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">Could not load this sale: {error}</div>;
  if (sale === undefined) return <p className="max-w-5xl mx-auto text-gray-600">Loading…</p>;
  if (sale === null) return (
    <div className="max-w-5xl mx-auto space-y-3">
      <p className="font-semibold">This sale is not in the archive.</p>
      <Link to="/past" className="text-primary-600 hover:underline">← Past Auctions</Link>
    </div>
  );

  const start = toDate(sale.startDate), end = toDate(sale.endDate);
  const pct = sellThrough(sale);
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <Link to="/past" className="text-sm text-primary-600 hover:underline">← Past Auctions</Link>
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">{sale.title}</h1>
        {start && end && <p className="text-gray-700"><span className="font-semibold">Date(s)</span> {format(start, 'M/d/yyyy')} - {format(end, 'M/d/yyyy')}</p>}
        {sale.venue && <p className="text-gray-700"><a href={mapLink(sale.venue)} target="_blank" rel="noopener noreferrer" className="hover:underline">📍 {sale.venue}</a></p>}
        {sale.description && <p className="text-gray-600 whitespace-pre-line">{sale.description}</p>}
        <p className="text-gray-900">
          <span className="font-semibold">{sale.lotCount} lots</span>
          {pct != null && <> · {sale.lotsSold} sold · <span className="font-semibold">{pct}% sell-through</span></>}
        </p>
        {sale.hasResults && <p className="text-xs text-gray-500">Prices shown are hammer prices, before buyer's premium.</p>}
      </header>

      {sale.hasResults && (
        <div className="flex gap-2" role="group" aria-label="Show lots">
          {(['all', 'sold', 'unsold'] as Show[]).map((k) => (
            <button key={k} onClick={() => setShow(k)}
              className={`text-sm px-3 py-1 rounded-full border ${show === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}>
              {k === 'all' ? `All (${lots?.length ?? 0})` : k === 'sold' ? `Sold (${(lots || []).filter((l) => l.result === 'sold').length})` : `Unsold (${(lots || []).filter((l) => l.result === 'unsold').length})`}
            </button>
          ))}
        </div>
      )}

      {lots === null ? <p className="text-gray-600">Loading lots…</p> : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {shown.map((l) => (
            <li key={l.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col">
              <Link to={`/past/${sale.id}/lots/${l.id}`} className="block aspect-square bg-gray-100">
                {l.images[0] ? <img src={l.images[0]} alt="" loading="lazy" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No photo</div>}
              </Link>
              <div className="p-3 flex flex-col flex-1">
                <p className="text-xs text-gray-500">Lot {l.lotNumber}</p>
                <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
                  <Link to={`/past/${sale.id}/lots/${l.id}`} className="hover:text-primary-600">{l.title}</Link>
                </h3>
                <div className="mt-auto pt-2">
                  {l.result === 'sold' && l.realizedCents != null ? (
                    <><div className="text-xs text-gray-500">Sold</div><div className="text-lg font-bold">{formatRealized(l.realizedCents)}</div></>
                  ) : l.result === 'unsold' ? (
                    <><div className="text-xs text-gray-500">Unsold</div><div className="text-sm text-gray-400">Did not sell</div></>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PastSaleResults;
